/** @see https://lucidatlassian.atlassian.net/wiki/spaces/MAR/pages/31253836275/A+B+Testing+-+Marketing */

import React, { FC, ReactNode, useCallback, useContext, useEffect, useState, PropsWithChildren } from 'react';

import { sendBeaconEvent, useCookie, useFeatureFlags, isBrowser } from '@lucid/utils';

import Box from '../box/box';
import ABTestContext from './ab-test-context';
import LayoutContext from '../context/layout-context';
import { getVariationAssignment } from './ab-test-helpers';
import ABTestBranch from './ab-test-branch';
import ABTestShield, { ShieldProps } from './ab-test-shield';
import type { ABTesting } from '../../types/types';

export interface ABTestProps {
  /** Test is visible without scrolling */
  isAboveTheFold?: boolean;
  /** Explicitly define the branches you'll be using to avoid discrepancies with feature flag variation allocations */
  branchesToRender: ABTesting.VariationKeys[];
  /** Allows the test wrapper to get additional styles. */
  className?: string;
  /** The children of the AB Test Component */
  children?: ReactNode;
  /** The default branch when test is inactive */
  defaultBranch?: ABTesting.VariationKeys;
  /** The branch that will be rendered in SSR */
  ssrBranch?: ABTesting.VariationKeys;
  /** String to match with corresponding Feature Flag to activate test and allocate traffic */
  testName: string;
  /** The method by which the test will be triggered */
  trigger?: ABTesting.Triggers;
  /** Will automatically trigger AB Test for unit testing */
  isTestMode?: boolean;
  /** Checks the conditions to determine if the trigger will fire. */
  willTriggerFire?: boolean;
}

export interface SubComponents {
  /** The AB Test branch */
  Branch: FC<PropsWithChildren<ABTesting.BranchProps>>;
  /** The Context for the AB Test */
  Context: typeof ABTestContext;
  /** Renders white shield over page until test has loaded */
  Shield: FC<PropsWithChildren<ShieldProps>>;
}

const ABTest: FC<PropsWithChildren<ABTestProps>> & SubComponents = ({
  branchesToRender = [],
  children = null,
  className,
  defaultBranch = 'T_A',
  isAboveTheFold = false,
  isTestMode = false,
  ssrBranch = 'T_A',
  testName,
  trigger = 'load',
  willTriggerFire = true,
}) => {
  const userId = useCookie('userId');
  const currentFeatureFlagTest = useFeatureFlags(testName);
  const storageKey = `abTest_${testName}`;
  const currentFeatureFlagTestOptions = currentFeatureFlagTest[testName];
  const isInitialActive = currentFeatureFlagTestOptions
    ? Boolean(currentFeatureFlagTestOptions.isActive && willTriggerFire)
    : true;
  const [isAssigned, setIsAssigned] = useState(false);
  const [isTestActive, setTestIsActive] = useState(isInitialActive);
  const [branch, setBranch] = useState<ABTesting.VariationKeys>(ssrBranch);
  const [hasTriggerFired, setHasTriggerFired] = useState(false);
  const [shouldShowShield, setShouldShowShield] = useState(true);
  const { abTesting } = useContext(LayoutContext);

  const isAdminUIActive = () => {
    if (isBrowser()) {
      return window.localStorage.getItem('showAbTestingUi') === '1';
    }

    return false;
  };

  const handleOnTrigger = useCallback(() => {
    // if hasTriggerFired is true, we don't want to send beacon info multiple times
    // if branch has a value, we negate it to not send multiple beacon events.
    if (hasTriggerFired || !branch) return;

    // We want to exit here when unit tests are run.
    if (isTestMode) return setHasTriggerFired(true);

    const event = {
      name: 'assignedToTest',
      userId: Number(userId) || -1,
      testName: testName,
      testArm: branch.replace(/_/g, '-'),
      teamName: 'Growth Front-end',
    };

    if (branch && isBrowser()) {
      window.localStorage.setItem(storageKey, branch);
    }

    if (isAdminUIActive()) {
      console.info(
        `%c Test: ${testName} - Would have fired the following event for Beacon: `,
        'color: #06A9F4; font-weight: bold',
        {
          ...event,
          isTestActive: !isTestActive ? 'No' : 'Yes',
        },
      );

      return setHasTriggerFired(true);
    }

    if (!isTestActive) return setHasTriggerFired(true);

    sendBeaconEvent(event)
      .then(response => {
        console.info('Beacon reported: ', response);
        return setHasTriggerFired(true);
      })
      .finally(() => {
        console.info('Beacon attempted');
        return setHasTriggerFired(true);
      });
  }, [branch, hasTriggerFired, isTestActive, isTestMode, storageKey, testName, userId]);

  const handleUpdateBranch = (updatedBranch: ABTesting.VariationKeys) => {
    setBranch(updatedBranch);
    return setHasTriggerFired(false);
  };

  const handleUpdateAdminUI = useCallback(() => {
    if (isAdminUIActive()) {
      const branches = currentFeatureFlagTestOptions?.variations ?? null;

      abTesting?.updateTest({
        branch,
        branches,
        branchesToRender,
        children,
        defaultBranch,
        description: currentFeatureFlagTestOptions?.description,
        hasTriggerFired,
        isAboveTheFold,
        isTestActive,
        testName,
        trigger,
        handleUpdateBranch,
      });
    }
  }, [
    abTesting,
    branch,
    branchesToRender,
    children,
    currentFeatureFlagTestOptions?.description,
    currentFeatureFlagTestOptions?.variations,
    defaultBranch,
    hasTriggerFired,
    isAboveTheFold,
    isTestActive,
    testName,
    trigger,
  ]);

  const setupTest = useCallback(() => {
    try {
      if (!currentFeatureFlagTestOptions) {
        throw Error(`AB Test not present in feature flag config: ${testName}.`);
      }

      if (willTriggerFire) {
        const { branch: assignedBranch, isTestActive: isActive } = getVariationAssignment({
          branchesToRender,
          testName,
          testBranches: currentFeatureFlagTestOptions.variations,
          storageKey,
          isDevMode: isAdminUIActive(),
        });

        setBranch(assignedBranch);
        setTestIsActive(isActive);
        return setIsAssigned(true);
      }

      setHasTriggerFired(true);
      setBranch(defaultBranch);
      return setTestIsActive(false);
    } catch (error) {
      console.error('AB Test setup error: ', error);
      return setTestIsActive(false);
    }
  }, [willTriggerFire, defaultBranch, testName, branchesToRender, currentFeatureFlagTestOptions, storageKey]);

  useEffect(() => {
    setupTest();
  }, [setupTest]);

  useEffect(() => {
    if (branch && isAssigned) {
      handleUpdateAdminUI();
      handleOnTrigger();
    }
  }, [branch, handleOnTrigger, isAssigned]);

  useEffect(() => {
    setShouldShowShield(isTestActive && isAboveTheFold && trigger === 'load' && !hasTriggerFired);

    const safetyTimeoutId = setTimeout(() => {
      setShouldShowShield(false);
    }, 2000);

    return () => {
      clearTimeout(safetyTimeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasTriggerFired]);

  const shouldRender = !isBrowser() && isTestActive ? hasTriggerFired : true;

  const abTestContext: ABTesting.ContextProps = {
    branch,
    branchesToRender,
    hasTriggerFired,
    shouldShowShield,
    testName,
    trigger,
    handleOnTrigger,
    handleUpdateBranch,
  };

  return (
    <ABTest.Context.Provider value={abTestContext}>
      <Box key={testName} className={className} id={`ab-test-${testName}`}>
        <ABTest.Shield shouldShowShield={shouldShowShield} />
        {shouldRender && children}
      </Box>
    </ABTest.Context.Provider>
  );
};

ABTest.Branch = ABTestBranch;
ABTest.Context = ABTestContext;
ABTest.Shield = ABTestShield;

export { ABTest };
export default ABTest;
